import type React from 'react';

import { AdornedInput, Adornment, FormField, FormHelp, Label, Paragraph, Spacer } from '@zillow/constellation';
import { CurrencyInput } from '@zillow/zrs-comet';

import type { Section } from '../../../models/index';
import YesAndNoExpansion from '../../Widgets/YesAndNoExpansion';
import { RENTERS_INSURANCE } from '../../../constants';
import InlineTooltip from '../../Widgets/InlineTooltip';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

const INITIAL_STATE = {
  [RENTERS_INSURANCE.FIELD_NAME.IS_ENABLED]: false,
  [RENTERS_INSURANCE.FIELD_NAME.MINIMUM_COVERAGE]: '',
};

export default function RentersInsurance({ section, setSectionValues }: Props): JSX.Element {
  function onNoRequireRentersInsurance(): void {
    setSectionValues(INITIAL_STATE);
  }

  function onYesRequireRentersInsurance(): void {
    setSectionValues({
      [RENTERS_INSURANCE.FIELD_NAME.IS_ENABLED]: true,
    });
  }

  function onMinimumCoverageChange(event: React.FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({ [RENTERS_INSURANCE.FIELD_NAME.MINIMUM_COVERAGE]: value });
  }

  return (
    <>
      <Paragraph marginBottom="xs" fontType="h6">
        Will you require tenants to obtain{' '}
        <InlineTooltip tooltipBodyText="In the lease, renters insurance means insurance that covers tenant’s personal property and damage to the property.">
          renters insurance
        </InlineTooltip>
        ?
      </Paragraph>
      <YesAndNoExpansion
        yesHandler={onYesRequireRentersInsurance}
        noHandler={onNoRequireRentersInsurance}
        yesActive={section.getFieldValue(RENTERS_INSURANCE.FIELD_NAME.IS_ENABLED)}
      >
        <Spacer marginBottom="sm" />
        <FormField
          label={<Label id="min-coverage-amt">Minimum coverage amount</Label>}
          control={
            <AdornedInput
              inputId="input-minimum-coverage-amt"
              leftAdornment={<Adornment>$</Adornment>}
              input={
                <CurrencyInput
                  aria-labelledby="min-coverage-amt"
                  data-test="min-coverage-amt"
                  value={section.getFieldValue(RENTERS_INSURANCE.FIELD_NAME.MINIMUM_COVERAGE)}
                  onChange={onMinimumCoverageChange}
                  onBlur={onMinimumCoverageChange}
                />
              }
            />
          }
          description={
            <FormHelp>An insurance broker can help you determine an appropriate amount of coverage</FormHelp>
          }
        />
      </YesAndNoExpansion>
    </>
  );
}
